import { useEffect, useRef } from "react";

interface Job {
  company: string;
  role: string;
  project?: string;
  date: string;
  location: string;
  tech: string[];
  descriptions: string[];
}

const EXPERIENCES: Job[] = [
  {
    company: "Dayforce",
    role: "Intermediate Software Developer",
    date: "April 2026 – Present",
    location: "Toronto, Canada",
    tech: ["C#", ".NET", "ASP.NET Core", "SQL", "Kafka", "Playwright", "Azure"],
    descriptions: [
      "Built an orchestrator-driven synchronization framework using Kafka event publishing and consumption, maintaining cross-environment data consistency for 500,000+ employee records.",
      "Led development of a migration workflow for 500,000+ interdependent records, introducing validations and processing optimizations that improved migration efficiency by 40%.",
      "Partnered with 5+ product teams to map cross-platform data flows and integrate multiple mutation entry points while preserving consistency and preventing regressions.",
      "Built a Playwright integration testing framework with automated regression monitoring and developer alerts, contributing to a 50% reduction in post-production defects.",
      "Introduced AI-assisted engineering workflows for parallel environment setup, implementation, testing, and investigation, accelerating delivery across concurrent development workstreams.",
    ],
  },
  {
    company: "Dayforce",
    role: "Software Developer Intern",
    date: "January 2025 – April 2026",
    location: "Toronto, Canada",
    tech: ["C#", ".NET", "ASP.NET Core", "SQL", "Kafka", "Azure", "PowerShell"],
    descriptions: [
      "Optimized service-to-service communication for large-scale employee migrations, reducing request latency and improving data migration performance by 50%.",
      "Supported migration of 200,000+ employee records to a new service architecture, preserving data integrity while improving system scalability and reliability.",
      "Designed and deployed V3 API endpoints to overcome payload limitations, increasing data throughput by 20% and stabilizing legacy-system integrations.",
      "Refactored PowerShell migration tooling and automation, reducing project setup time from 10 minutes to 2 minutes and improving developer productivity.",
    ],
  },
  {
    company: "EllisDon",
    role: "Full Stack Developer Intern",
    date: "September 2023 – August 2024",
    location: "Mississauga, Canada",
    tech: ["Java", "Spring", "Node.js", "React", "TypeScript", "Datadog", "Azure", "GCP"],
    descriptions: [
      "Modernized the legacy Request for Information (RFI) module by developing new API endpoints, cutting system latency by 20% and improving reliability for thousands of users.",
      "Built real-time schema and data management tooling with Excel export capabilities, streamlining reporting and data workflows for 1,000+ subcontractors.",
      "Developed 15+ reusable UI components, including a widely adopted Rich Text Editor, improving consistency and accelerating development across multiple applications.",
    ],
  },
];

const Experience = () => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="experience reveal" ref={ref}>
      <p className="section-label">Experience</p>
      <div className="experience-list">
        {EXPERIENCES.map((job) => (
          <div className="experience-item" key={`${job.company}-${job.role}`}>
            <div className="experience-item-header">
              <div className="experience-item-left">
                <h2 className="experience-item-company">{job.company}</h2>
                <div className="experience-item-role">{job.role}</div>
                {job.project && (
                  <div className="experience-item-project">{job.project}</div>
                )}
                <div className="experience-item-tech">
                  {job.tech.map((t) => (
                    <span key={t} className="experience-item-chip">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="experience-item-right">
                <div className="experience-item-date">{job.date}</div>
                <div className="experience-item-location">{job.location}</div>
              </div>
            </div>
            <ul className="experience-item-bullets">
              {job.descriptions.map((desc, i) => (
                <li key={i}>{desc}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Experience;
